import {
  useNavigate,
  useParams as useTanStackParams,
  useRouter as useTanStackRouter,
  useRouterState,
} from '@tanstack/react-router';
import { useMemo, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react';

function normalizeHref(href: string) {
  return href || '/';
}

function isPlainLeftClick(event: MouseEvent<HTMLAnchorElement>) {
  return event.button === 0 && !event.metaKey && !event.altKey && !event.ctrlKey && !event.shiftKey;
}

export function useRouter() {
  const navigate = useNavigate();
  const router = useTanStackRouter();

  return useMemo(
    () => ({
      push: (href: string) => void navigate({ to: normalizeHref(href) }),
      replace: (href: string) => void navigate({ to: normalizeHref(href), replace: true }),
      back: () => window.history.back(),
      forward: () => window.history.forward(),
      refresh: () => void router.invalidate(),
      prefetch: () => Promise.resolve(undefined),
    }),
    [navigate, router],
  );
}

export function usePathname() {
  return useRouterState({ select: (state) => state.location.pathname });
}

export function useSearchParams() {
  const href = useRouterState({ select: (state) => state.location.href });

  return useMemo(() => {
    const query = href.includes('?') ? href.split('?')[1]?.split('#')[0] : '';
    return new URLSearchParams(query);
  }, [href]);
}

export function useParams() {
  return useTanStackParams({ strict: false });
}

interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  href: string;
  replace?: boolean;
  children?: ReactNode;
}

export function Link({ href, replace = false, onClick, children, ...props }: LinkProps) {
  const router = useRouter();

  return (
    <a
      href={href}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented || !isPlainLeftClick(event) || href.startsWith('http')) return;
        event.preventDefault();
        if (replace) {
          router.replace(href);
        } else {
          router.push(href);
        }
      }}
      {...props}
    >
      {children}
    </a>
  );
}

export function redirect(href: string): never {
  if (typeof window !== 'undefined') {
    window.location.replace(href);
  }
  throw new Error(`Redirected to ${href}`);
}

export default Link;
